import api from "@/api/api";

import { validateContactForm } from '@/helpers/validation';
import { ContactFormData } from '@/models/customersTypes';
import React, { useState } from 'react';
import Modal from "./Modal";
import { track } from '@/helpers/analytics';

const ContactForm: React.FC = () => {
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        subject: '',
        message: '',
        concern: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            const validationErrors = validateContactForm(formData);
            setFormErrors(validationErrors);

            if (Object.keys(validationErrors).length > 0) return;

            await api.interests.sendContactForm(formData);
            track('contact-sent', { concern: formData.concern || 'none' });

            setShowModal(true);
            // Only once sent: a failed check or send must keep what was typed.
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: '',
                concern: '',
            })
        } catch (error) {
            console.error(error);
            track('form-error', { form: 'contact' });
            alert('Fehler beim Senden des Formulars.');
        }
    };

    return (
        <section className="w-full max-w-3xl mx-auto p-6 bg-white rounded-xl shadow-md">
            <h2 className="text-2xl font-bold mb-6 text-textColor">Kontaktformular</h2>

            <div className="space-y-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                    />
                    {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                    <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                    />
                    {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Anliegen</label>
                    <select
                        name="concern"
                        required
                        value={formData.concern}
                        onChange={handleChange}
                        className="w-full border border-gray-300 p-3 rounded bg-white focus:outline-none focus:ring-2 focus:ring-lineGreen"
                    >
                        <option value="" disabled>Bitte wählen</option>
                        <option value="Support">Support (Probleme im Laden, Bezahlung, App/Konto)</option>
                        <option value="Allgemeine Anfrage">Allgemeine Anfrage</option>
                    </select>
                    {formErrors.concern && <p className="text-red-500 text-sm mt-1">{formErrors.concern}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Betreff</label>
                    <input
                        type="text"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                    />
                    {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht</label>
                    <textarea
                        name="message"
                        rows={5}
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                    />
                    {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                </div>

                <button
                    type="button"
                    onClick={handleSubmit}
                    className="bg-lineGreen text-white px-6 py-2 rounded hover:bg-primaryDark transition"
                >
                    Absenden
                </button>
            </div>

            <Modal show={showModal} onClose={() => setShowModal(false)} />
        </section>
    );
};

export default ContactForm;
